// File: frontend/src/utils/usePageMeta.js
//
// Lightweight SEO helper — the project has no react-helmet, so we set the
// document title and the standard meta/OG tags imperatively. Tags are created
// on demand and the previous title is restored on unmount so navigating between
// pages doesn't leave stale metadata behind.
//
// The public pages are also rendered to static HTML at build time
// (scripts/prerender.js). That script copies whatever this hook wrote into the
// document head, so these values are also what crawlers see.
//
// `canonical` sets <link rel="canonical"> and og:url, and `noindex` sets
// <meta name="robots" content="noindex">. Both are put back on unmount, because
// a canonical or noindex left over from the previous page is worse than none.

import { useEffect } from "react";

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Set an attribute on a head element (creating it if needed); returns an undo. */
function setHeadAttr(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) { el = create(); document.head.appendChild(el); }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    if (created) el.remove();
    else if (prev != null) el.setAttribute(attr, prev);
  };
}

const makeEl = (tag, attrs) => () => {
  const n = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
  return n;
};

export default function usePageMeta({ title, description, ogImage, themeColor, canonical, noindex = false } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
      setMeta("name", "twitter:card", "summary_large_image");
      setMeta("property", "og:type", "website");
    }
    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }
    if (themeColor) setMeta("name", "theme-color", themeColor);

    const undo = [];
    if (canonical) {
      undo.push(setHeadAttr('link[rel="canonical"]', makeEl("link", { rel: "canonical" }), "href", canonical));
      undo.push(setHeadAttr('meta[property="og:url"]', makeEl("meta", { property: "og:url" }), "content", canonical));
    }
    if (noindex) {
      undo.push(setHeadAttr('meta[name="robots"]', makeEl("meta", { name: "robots" }), "content", "noindex"));
    }

    return () => {
      document.title = prevTitle;
      undo.reverse().forEach((fn) => fn());
    };
  }, [title, description, ogImage, themeColor, canonical, noindex]);
}
